import {useEffect, useRef, useState} from "react"
import {useApiService} from "./useApiService"
import {fetchLeagueFixtures} from "../services/competitions"
import {FixtureResponse} from "../types/fixture"

export type LeagueFixturesState = {
    fixtures: FixtureResponse[]
    loading: boolean
    failed: boolean
}

const EMPTY: LeagueFixturesState = {fixtures: [], loading: false, failed: false}

/**
 * The matches a competition still has to play.
 *
 * Read from the football data provider, which holds the calendar: nothing of the prediction
 * provider's is touched until one of these matches is actually picked for analysis.
 */
export const useLeagueFixtures = (leagueId?: number): LeagueFixturesState => {
    const apiService = useApiService()
    const [state, setState] = useState<LeagueFixturesState>(EMPTY)
    // Bumped per search so a slow answer cannot overwrite the results of a later one
    const runRef = useRef(0)

    useEffect(() => {
        if (!leagueId) {
            runRef.current += 1
            setState(EMPTY)
            return
        }

        const run = ++runRef.current
        setState({fixtures: [], loading: true, failed: false})

        ;(async () => {
            try {
                const fixtures = await fetchLeagueFixtures(() => apiService.client(true, false), leagueId)

                if (run === runRef.current)
                    setState({fixtures, loading: false, failed: false})
            } catch (error) {
                if (run === runRef.current)
                    setState({fixtures: [], loading: false, failed: true})
            }
        })()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [leagueId])

    return state
}
